import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  Package, AlertTriangle, Clock, Plus, Search, Pill, ShoppingCart,
  Edit2, Trash2, RefreshCw, CalendarDays, Building2, Phone, IndianRupee,
  ArrowUpDown, AlertCircle, CheckCircle, Loader2, Upload, Download
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClinicInventoryPage() {
  const [inventory, setInventory] = useState([]);
  const fileInputRef = useRef(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [alerts, setAlerts] = useState({ low_stock: [], expiring_soon: [], expired: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('alerts');
  const [searchQuery, setSearchQuery] = useState('');
  const [productTypeFilter, setProductTypeFilter] = useState('all');
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [adjustingItem, setAdjustingItem] = useState(null);
  
  // Vendor/Product search states
  const [approvedVendors, setApprovedVendors] = useState([]);
  const [vendorProducts, setVendorProducts] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorSearchQuery, setVendorSearchQuery] = useState('');
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [useManualEntry, setUseManualEntry] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    product_id: '',
    product_type: 'medicine',
    product_name: '',
    vendor_id: '',
    vendor_name: '',
    current_stock: 0,
    min_stock: 0,
    batch_number: '',
    batch_expiry: '',
    unit_price: 0,
    notes: ''
  });
  
  const [adjustData, setAdjustData] = useState({
    quantity_change: 0,
    reason: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [inventoryRes, alertsRes] = await Promise.all([
        API.get('/clinic-inventory'),
        API.get('/clinic-inventory/alerts')
      ]);
      setInventory(inventoryRes.data);
      setAlerts(alertsRes.data);
    } catch (err) {
      console.error('Failed to fetch inventory:', err);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch approved vendors when dialog opens
  const fetchApprovedVendors = async (search = '') => {
    setLoadingVendors(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const res = await API.get(`/clinic-inventory/approved-vendors?${params}`);
      setApprovedVendors(res.data);
    } catch (err) {
      console.error('Failed to fetch vendors:', err);
      setApprovedVendors([]);
    } finally {
      setLoadingVendors(false);
    }
  };

  // Fetch products for selected vendor
  const fetchVendorProducts = async (vendorId, search = '') => {
    setLoadingProducts(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const res = await API.get(`/clinic-inventory/vendor-products/${vendorId}?${params}`);
      setVendorProducts(res.data.products || []);
    } catch (err) {
      console.error('Failed to fetch products:', err);
      setVendorProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Handle vendor selection
  const handleVendorSelect = (vendor) => {
    setSelectedVendor(vendor);
    setFormData({
      ...formData,
      vendor_id: vendor.id,
      vendor_name: vendor.name,
      product_type: vendor.vendor_type === 'pharma' || vendor.vendor_type === 'chemist' ? 'medicine' : 'inventory_product'
    });
    fetchVendorProducts(vendor.id);
    setProductSearchQuery('');
  };

  // Handle product selection
  const handleProductSelect = (product) => {
    setFormData({
      ...formData,
      product_id: product.id,
      product_name: product.name,
      product_type: product.type,
      unit_price: product.b2b_price || 0,
      batch_expiry: product.batch_expiry ? product.batch_expiry.split('T')[0] : ''
    });
  };

  const handleAddItem = async () => {
    if (!formData.product_name || !formData.vendor_name) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      if (editingItem) {
        await API.put(`/clinic-inventory/${editingItem.id}`, formData);
        toast.success('Item updated');
      } else {
        await API.post('/clinic-inventory', formData);
        toast.success('Item added to inventory');
      }
      setShowAddDialog(false);
      setEditingItem(null);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save item');
    }
  };

  // Download template for bulk upload
  const downloadInventoryTemplate = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/clinic-inventory/template/download`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('clinic_token')}` }
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'inventory_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded');
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  // Bulk upload inventory from Excel/CSV
  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingFile(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/clinic-inventory/bulk-upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('clinic_token')}` },
        body: formDataUpload
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }
      
      const result = await response.json();
      toast.success(result.message);
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAdjustStock = async () => {
    if (!adjustingItem || adjustData.quantity_change === 0) {
      toast.error('Please enter a valid quantity change');
      return;
    }
    
    try {
      await API.post(`/clinic-inventory/${adjustingItem.id}/adjust`, adjustData);
      toast.success('Stock adjusted');
      setShowAdjustDialog(false);
      setAdjustingItem(null);
      setAdjustData({ quantity_change: 0, reason: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to adjust stock');
    }
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await API.delete(`/clinic-inventory/${item.id}`);
      toast.success('Item deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete item');
    }
  };

  const openEditDialog = (item) => {
    setFormData({
      product_id: item.product_id,
      product_type: item.product_type,
      product_name: item.product_name,
      vendor_id: item.vendor_id,
      vendor_name: item.vendor_name,
      current_stock: item.current_stock,
      min_stock: item.min_stock,
      batch_number: item.batch_number || '',
      batch_expiry: item.batch_expiry ? item.batch_expiry.split('T')[0] : '',
      unit_price: item.unit_price || 0,
      notes: item.notes || ''
    });
    setEditingItem(item);
    setShowAddDialog(true);
  };

  const openAdjustDialog = (item) => {
    setAdjustingItem(item);
    setAdjustData({ quantity_change: 0, reason: '' });
    setShowAdjustDialog(true);
  };

  const resetForm = () => {
    setFormData({
      product_id: '',
      product_type: 'medicine',
      product_name: '',
      vendor_id: '',
      vendor_name: '',
      current_stock: 0,
      min_stock: 0,
      batch_number: '',
      batch_expiry: '',
      unit_price: 0,
      notes: ''
    });
    setSelectedVendor(null);
    setVendorProducts([]);
    setVendorSearchQuery('');
    setProductSearchQuery('');
    setUseManualEntry(false);
  };

  // Open Add dialog with vendor fetch
  const openAddDialog = () => {
    resetForm();
    setEditingItem(null);
    setShowAddDialog(true);
    fetchApprovedVendors();
  };

  const filteredInventory = inventory.filter(item => {
    const matchesSearch = !searchQuery || 
      item.product_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.vendor_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.batch_number || '').toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesType = productTypeFilter === 'all' || item.product_type === productTypeFilter;
    
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="clinic-inventory-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>Clinic Inventory</h1>
          <p className="text-slate-500 text-sm">Track stock levels, expiry dates, and reorder alerts</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={downloadInventoryTemplate} data-testid="inventory-template-btn">
            <Download className="w-4 h-4 mr-2" /> Download Template
          </Button>
          <input type="file" ref={fileInputRef} accept=".csv,.xlsx,.xls" onChange={handleBulkUpload} className="hidden" />
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploadingFile} data-testid="inventory-upload-btn">
            {uploadingFile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploadingFile ? 'Uploading...' : 'Bulk Upload'}
          </Button>
          <Button onClick={openAddDialog} data-testid="add-inventory-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Item
          </Button>
        </div>
      </div>

      {/* Alert Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={`border-l-4 ${alerts.summary.low_stock_count > 0 ? 'border-l-orange-500 bg-orange-50/50' : 'border-l-green-500'}`}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${alerts.summary.low_stock_count > 0 ? 'bg-orange-100' : 'bg-green-100'}`}>
              <AlertTriangle className={`w-6 h-6 ${alerts.summary.low_stock_count > 0 ? 'text-orange-600' : 'text-green-600'}`} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Low Stock Items</p>
              <p className="text-2xl font-bold">{alerts.summary.low_stock_count || 0}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`border-l-4 ${alerts.summary.expiring_soon_count > 0 ? 'border-l-yellow-500 bg-yellow-50/50' : 'border-l-green-500'}`}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${alerts.summary.expiring_soon_count > 0 ? 'bg-yellow-100' : 'bg-green-100'}`}>
              <Clock className={`w-6 h-6 ${alerts.summary.expiring_soon_count > 0 ? 'text-yellow-600' : 'text-green-600'}`} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Expiring Soon (90 days)</p>
              <p className="text-2xl font-bold">{alerts.summary.expiring_soon_count || 0}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`border-l-4 ${alerts.summary.expired_count > 0 ? 'border-l-red-500 bg-red-50/50' : 'border-l-green-500'}`}>
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${alerts.summary.expired_count > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
              <AlertCircle className={`w-6 h-6 ${alerts.summary.expired_count > 0 ? 'text-red-600' : 'text-green-600'}`} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Expired Items</p>
              <p className="text-2xl font-bold">{alerts.summary.expired_count || 0}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-teal-500">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-teal-100">
              <Package className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Items</p>
              <p className="text-2xl font-bold">{inventory.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="alerts" data-testid="tab-alerts">
            <AlertTriangle className="w-4 h-4 mr-2" /> Alerts ({alerts.summary.total_alerts || 0})
          </TabsTrigger>
          <TabsTrigger value="inventory" data-testid="tab-inventory">
            <Package className="w-4 h-4 mr-2" /> All Inventory ({inventory.length})
          </TabsTrigger>
        </TabsList>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-6">
          {/* Low Stock Alerts */}
          {alerts.low_stock?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-orange-700">
                  <AlertTriangle className="w-5 h-5" /> Low Stock - Reorder Required
                </CardTitle>
                <CardDescription>Items below minimum stock level</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {alerts.low_stock.map((item) => (
                    <div 
                      key={item.id} 
                      className="p-3 sm:p-4 border border-orange-200 rounded-lg bg-orange-50/30"
                      data-testid={`low-stock-item-${item.id}`}
                    >
                      <div className="flex items-start gap-3 sm:gap-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          {item.product_type === 'medicine' ? (
                            <Pill className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                          ) : (
                            <Package className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-800 text-sm sm:text-base truncate">{item.product_name}</h3>
                          <p className="text-xs sm:text-sm text-slate-500 truncate">
                            {item.vendor_name} • Batch: {item.batch_number || 'N/A'}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-orange-700 border-orange-300 text-xs">
                              Stock: {item.current_stock} / Min: {item.min_stock}
                            </Badge>
                            <Badge variant="destructive" className="text-[10px] sm:text-xs">
                              Need {item.shortage_quantity}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      
                      {/* Action row */}
                      <div className="flex flex-wrap items-center justify-between gap-2 mt-3 pt-3 border-t border-orange-200/50">
                      {/* Cheapest Vendor Suggestion */}
                        {item.cheapest_vendor && (
                          <div className="flex items-center gap-2 text-xs sm:text-sm">
                            <Building2 className="w-4 h-4 text-green-600" />
                            <span className="text-green-700 font-medium">{item.cheapest_vendor.vendor_name}</span>
                            <span className="text-green-600 font-bold">
                              ₹{item.cheapest_vendor.b2b_price}/unit
                            </span>
                          </div>
                        )}
                        <Button size="sm" variant="outline" onClick={() => openAdjustDialog(item)} className="h-8 text-xs">
                          <ArrowUpDown className="w-4 h-4 mr-1" /> Adjust
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Expiring Soon Alerts */}
          {alerts.expiring_soon?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-yellow-700">
                  <Clock className="w-5 h-5" /> Expiring Soon (Within 90 Days)
                </CardTitle>
                <CardDescription>Items that need to be used or disposed</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {alerts.expiring_soon.map((item) => (
                    <div 
                      key={item.id} 
                      className="p-4 border border-yellow-200 rounded-lg bg-yellow-50/30 flex items-center justify-between"
                      data-testid={`expiring-item-${item.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                          <CalendarDays className="w-6 h-6 text-yellow-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-800">{item.product_name}</h3>
                          <p className="text-sm text-slate-500">
                            {item.vendor_name} • Batch: {item.batch_number || 'N/A'}
                          </p>
                          <div className="flex items-center gap-4 mt-1">
                            <Badge variant="outline" className="text-yellow-700 border-yellow-300">
                              {item.days_until_expiry} days until expiry
                            </Badge>
                            <span className="text-xs text-slate-400">
                              Expiry: {item.batch_expiry ? new Date(item.batch_expiry).toLocaleDateString() : 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Stock: {item.current_stock}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Expired Items */}
          {alerts.expired?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-5 h-5" /> Expired Items - Action Required
                </CardTitle>
                <CardDescription>These items should be disposed of</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {alerts.expired.map((item) => (
                    <div 
                      key={item.id} 
                      className="p-4 border border-red-200 rounded-lg bg-red-50/30 flex items-center justify-between"
                      data-testid={`expired-item-${item.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                          <AlertCircle className="w-6 h-6 text-red-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-800">{item.product_name}</h3>
                          <p className="text-sm text-slate-500">
                            {item.vendor_name} • Batch: {item.batch_number || 'N/A'}
                          </p>
                          <Badge variant="destructive" className="mt-1">
                            Expired {item.days_since_expiry} days ago
                          </Badge>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteItem(item)}>
                          <Trash2 className="w-4 h-4 mr-1" /> Remove
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* No Alerts Message */}
          {!alerts.low_stock?.length && !alerts.expiring_soon?.length && !alerts.expired?.length && (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                <h3 className="text-xl font-semibold text-slate-800 mb-2">All Clear!</h3>
                <p className="text-slate-500">No inventory alerts at this time</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* All Inventory Tab */}
        <TabsContent value="inventory">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <CardTitle>All Inventory Items</CardTitle>
                <div className="flex gap-3">
                  <div className="relative flex-1 md:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search items..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                      data-testid="inventory-search"
                    />
                  </div>
                  <Select value={productTypeFilter} onValueChange={setProductTypeFilter}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="medicine">Medicines</SelectItem>
                      <SelectItem value="inventory_product">Equipment</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={fetchData}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredInventory.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No inventory items found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Product</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Vendor</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Stock</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Min Stock</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Batch</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Expiry</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInventory.map((item) => (
                        <tr key={item.id} className="border-b hover:bg-slate-50" data-testid={`inventory-row-${item.id}`}>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.product_type === 'medicine' ? 'bg-pink-100' : 'bg-orange-100'}`}>
                                {item.product_type === 'medicine' ? (
                                  <Pill className="w-4 h-4 text-pink-600" />
                                ) : (
                                  <Package className="w-4 h-4 text-orange-600" />
                                )}
                              </div>
                              <span className="font-medium">{item.product_name}</span>
                              {item.source === 'catalog' && <Badge className="bg-teal-100 text-teal-700 text-[10px] ml-2">Catalog</Badge>}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-600">{item.vendor_name}</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`font-bold ${item.is_low_stock ? 'text-orange-600' : 'text-slate-800'}`}>
                              {item.current_stock}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center text-slate-500">{item.min_stock}</td>
                          <td className="py-3 px-4 text-sm text-slate-600">{item.batch_number || '-'}</td>
                          <td className="py-3 px-4 text-sm">
                            {item.batch_expiry ? (
                              <span className={item.is_expired ? 'text-red-600' : item.is_expiring_soon ? 'text-yellow-600' : 'text-slate-600'}>
                                {new Date(item.batch_expiry).toLocaleDateString()}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {item.is_expired ? (
                              <Badge variant="destructive">Expired</Badge>
                            ) : item.is_expiring_soon ? (
                              <Badge className="bg-yellow-100 text-yellow-700">Expiring</Badge>
                            ) : item.is_low_stock ? (
                              <Badge className="bg-orange-100 text-orange-700">Low Stock</Badge>
                            ) : (
                              <Badge className="bg-green-100 text-green-700">OK</Badge>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex justify-end gap-2">
                              <Button size="sm" variant="ghost" onClick={() => openAdjustDialog(item)}>
                                <ArrowUpDown className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => openEditDialog(item)}>
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteItem(item)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Inventory Item' : 'Add Inventory Item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Smart Vendor/Product Selection (only for new items) */}
            {!editingItem && !useManualEntry && (
              <>
                {/* Vendor Selection */}
                <div className="space-y-2">
                  <Label>Select Vendor (Approved)</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={vendorSearchQuery}
                      onChange={(e) => {
                        setVendorSearchQuery(e.target.value);
                        fetchApprovedVendors(e.target.value);
                      }}
                      placeholder="Search approved vendors..."
                      className="pl-10"
                    />
                  </div>
                  {loadingVendors ? (
                    <div className="flex items-center gap-2 text-sm text-slate-500 p-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading vendors...
                    </div>
                  ) : approvedVendors.length > 0 ? (
                    <div className="border rounded-lg max-h-32 overflow-y-auto">
                      {approvedVendors.map((vendor) => (
                        <div
                          key={vendor.id}
                          onClick={() => handleVendorSelect(vendor)}
                          className={`p-2 cursor-pointer hover:bg-slate-50 flex items-center gap-2 ${selectedVendor?.id === vendor.id ? 'bg-teal-50 border-l-2 border-l-teal-500' : ''}`}
                        >
                          <Building2 className="w-4 h-4 text-slate-400" />
                          <div>
                            <p className="font-medium text-sm">{vendor.name}</p>
                            <p className="text-xs text-slate-500">{vendor.vendor_type === 'pharma' ? 'Pharmaceutical' : vendor.vendor_type === 'chemist' ? 'Chemist' : 'Equipment'}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 p-2">No approved vendors found. <button className="text-teal-600 underline" onClick={() => setUseManualEntry(true)}>Enter manually</button></p>
                  )}
                </div>

                {/* Product Selection (if vendor selected) */}
                {selectedVendor && (
                  <div className="space-y-2">
                    <Label>Select Product from {selectedVendor.name}</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={productSearchQuery}
                        onChange={(e) => {
                          setProductSearchQuery(e.target.value);
                          fetchVendorProducts(selectedVendor.id, e.target.value);
                        }}
                        placeholder="Search products..."
                        className="pl-10"
                      />
                    </div>
                    {loadingProducts ? (
                      <div className="flex items-center gap-2 text-sm text-slate-500 p-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading products...
                      </div>
                    ) : vendorProducts.length > 0 ? (
                      <div className="border rounded-lg max-h-40 overflow-y-auto">
                        {vendorProducts.map((product) => (
                          <div
                            key={product.id}
                            onClick={() => handleProductSelect(product)}
                            className={`p-2 cursor-pointer hover:bg-slate-50 flex items-center justify-between ${formData.product_id === product.id ? 'bg-teal-50 border-l-2 border-l-teal-500' : ''}`}
                          >
                            <div className="flex items-center gap-2">
                              {product.type === 'medicine' ? <Pill className="w-4 h-4 text-pink-500" /> : <Package className="w-4 h-4 text-orange-500" />}
                              <div>
                                <p className="font-medium text-sm">{product.name}</p>
                                <p className="text-xs text-slate-500">{product.generic_name || product.brand || product.category}</p>
                              </div>
                            </div>
                            <span className="text-sm font-medium text-green-600">₹{product.b2b_price}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 p-2">No products found</p>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-2 text-sm">
                  <button className="text-teal-600 underline" onClick={() => setUseManualEntry(true)}>
                    Or enter product details manually
                  </button>
                </div>
              </>
            )}

            {/* Manual Entry Form or Edit Form */}
            {(useManualEntry || editingItem) && (
              <>
                {!editingItem && (
                  <button className="text-sm text-teal-600 underline mb-2" onClick={() => setUseManualEntry(false)}>
                    ← Back to vendor selection
                  </button>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Product Type</Label>
                    <Select value={formData.product_type} onValueChange={(v) => setFormData({ ...formData, product_type: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="medicine">Medicine</SelectItem>
                        <SelectItem value="inventory_product">Equipment</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Product Name *</Label>
                    <Input 
                      value={formData.product_name} 
                      onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                      placeholder="e.g., Paracetamol 500mg"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Vendor Name *</Label>
                  <Input 
                    value={formData.vendor_name} 
                    onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                    placeholder="Vendor name"
                  />
                </div>
              </>
            )}

            {/* Common Fields (always shown when there's a product selected or manual entry) */}
            {(formData.product_name || useManualEntry || editingItem) && (
              <>
                {/* Selected product summary */}
                {formData.product_name && !useManualEntry && !editingItem && (
                  <div className="p-3 bg-teal-50 rounded-lg border border-teal-100">
                    <p className="text-sm font-medium text-teal-800">Selected: {formData.product_name}</p>
                    <p className="text-xs text-teal-600">from {formData.vendor_name} • ₹{formData.unit_price}/unit</p>
                  </div>
                )}
            
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Current Stock</Label>
                    <Input 
                      type="number"
                      value={formData.current_stock} 
                      onChange={(e) => setFormData({ ...formData, current_stock: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Min Stock (Alert)</Label>
                    <Input 
                      type="number"
                      value={formData.min_stock} 
                      onChange={(e) => setFormData({ ...formData, min_stock: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Unit Price (₹)</Label>
                    <Input 
                      type="number"
                      value={formData.unit_price} 
                      onChange={(e) => setFormData({ ...formData, unit_price: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                </div>
            
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Batch Number</Label>
                    <Input 
                      value={formData.batch_number} 
                      onChange={(e) => setFormData({ ...formData, batch_number: e.target.value })}
                      placeholder="e.g., BTH-2024-001"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Batch Expiry Date</Label>
                    <Input 
                      type="date"
                      value={formData.batch_expiry} 
                      onChange={(e) => setFormData({ ...formData, batch_expiry: e.target.value })}
                    />
                  </div>
                </div>
            
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea 
                    value={formData.notes} 
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes..."
                    rows={2}
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddDialog(false); setEditingItem(null); resetForm(); }}>Cancel</Button>
            <Button onClick={handleAddItem} disabled={!formData.product_name || !formData.vendor_name}>
              {editingItem ? 'Save Changes' : 'Add Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust Stock Dialog */}
      <Dialog open={showAdjustDialog} onOpenChange={setShowAdjustDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Adjust Stock</DialogTitle>
          </DialogHeader>
          {adjustingItem && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{adjustingItem.product_name}</p>
                <p className="text-sm text-slate-500">Current Stock: {adjustingItem.current_stock}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Quantity Change</Label>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setAdjustData({ ...adjustData, quantity_change: adjustData.quantity_change - 1 })}
                  >
                    -
                  </Button>
                  <Input 
                    type="number"
                    value={adjustData.quantity_change} 
                    onChange={(e) => setAdjustData({ ...adjustData, quantity_change: parseInt(e.target.value) || 0 })}
                    className="text-center"
                  />
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setAdjustData({ ...adjustData, quantity_change: adjustData.quantity_change + 1 })}
                  >
                    +
                  </Button>
                </div>
                <p className="text-sm text-center">
                  New Stock: <span className="font-bold">{adjustingItem.current_stock + adjustData.quantity_change}</span>
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Reason</Label>
                <Select value={adjustData.reason} onValueChange={(v) => setAdjustData({ ...adjustData, reason: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="received_stock">Received New Stock</SelectItem>
                    <SelectItem value="sold">Sold/Dispensed</SelectItem>
                    <SelectItem value="damaged">Damaged/Expired</SelectItem>
                    <SelectItem value="correction">Inventory Correction</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdjustDialog(false)}>Cancel</Button>
            <Button onClick={handleAdjustStock}>Adjust Stock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
